import { useState } from 'react';

export function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d4i161ttnoqs9bvm9u1g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gradient-to-b from-white to-rose-50 border-t border-rose-100">
      {/* Newsletter Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="bg-gradient-to-br from-rose-800 to-red-900 rounded-3xl p-12 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl mx-auto mb-6">
              <i className="ri-mail-line text-3xl text-white"></i>
            </div>
            <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Cinzel', serif" }}>
              Stay Updated on <strong>Clarity</strong> Insights
            </h3>
            <p className="text-rose-100 mb-8 text-lg">
              Subscribe to receive valuable insights and tips for your arranged marriage journey
            </p>
            
            <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto">
              <div className="flex gap-3">
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-6 py-3 rounded-full border-2 border-white/30 bg-white/10 text-white placeholder-white/60 focus:outline-none focus:border-white transition-colors text-sm"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-white text-rose-800 rounded-full font-semibold hover:bg-rose-50 transition-all duration-300 disabled:opacity-50 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>
              
              {submitStatus === 'success' && (
                <p className="text-white mt-3 text-sm">Thank you for subscribing!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-rose-200 mt-3 text-sm">Something went wrong. Please try again.</p>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 flex items-center justify-center">
                <i className="ri-heart-3-fill text-3xl text-rose-800"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Cinzel', serif" }}>
                Clarity for AM
              </h4>
            </div>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Expert guidance for your <strong className="text-rose-700">arranged marriage</strong> journey. Development through <strong className="text-rose-700">clarity</strong>, not relationship advice.
            </p>
            <div className="flex gap-3">
              <a href="#" className="w-10 h-10 flex items-center justify-center bg-rose-100 hover:bg-rose-200 rounded-full transition-colors cursor-pointer">
                <i className="ri-quora-fill text-xl text-rose-800"></i>
              </a>
              <a href="#" className="w-10 h-10 flex items-center justify-center bg-rose-100 hover:bg-rose-200 rounded-full transition-colors cursor-pointer">
                <i className="ri-reddit-fill text-xl text-rose-800"></i>
              </a>
              <a href="#" className="w-10 h-10 flex items-center justify-center bg-rose-100 hover:bg-rose-200 rounded-full transition-colors cursor-pointer">
                <i className="ri-twitter-x-fill text-xl text-rose-800"></i>
              </a>
              <a href="#" className="w-10 h-10 flex items-center justify-center bg-rose-100 hover:bg-rose-200 rounded-full transition-colors cursor-pointer">
                <i className="ri-newspaper-line text-xl text-rose-800"></i>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold text-gray-900 mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <a href="#services" className="text-gray-600 hover:text-rose-800 transition-colors cursor-pointer">
                  Services
                </a>
              </li>
              <li>
                <a href="#consultation" className="text-gray-600 hover:text-rose-800 transition-colors cursor-pointer">
                  Book Consultation
                </a>
              </li>
              <li>
                <button
                  onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                  className="text-gray-600 hover:text-rose-800 transition-colors cursor-pointer"
                >
                  AI Assistant
                </button>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold text-gray-900 mb-4">Services</h4>
            <ul className="space-y-3">
              <li>
                <a href="#services" className="text-gray-600 hover:text-rose-800 transition-colors cursor-pointer">
                  1-on-1 Sessions
                </a>
              </li>
              <li>
                <a href="#services" className="text-gray-600 hover:text-rose-800 transition-colors cursor-pointer">
                  Clarity Classes
                </a>
              </li>
              <li>
                <a href="#services" className="text-gray-600 hover:text-rose-800 transition-colors cursor-pointer">
                  Clarity Clips
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-rose-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-sm">
              © 2024 Clarity for Arranged Marriage. All rights reserved.
            </p>
            <a 
              href="https://readdy.ai/?origin=logo" 
              target="_blank" 
              rel="nofollow noopener noreferrer"
              className="text-gray-600 hover:text-rose-800 text-sm transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
